import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { KpiValueItem } from '../../kpi/hooks.js';
import type { ScenarioRow } from '../../scenario/types.js';

/**
 * "{Main metric} by Day" — right chart tile on the Daily Dashboard's
 * bottom row. Mirrors the legacy `sec_065` widget: single-series bar
 * chart of the configured main metric's per-day quantity, summed
 * across the filtered scenario set.
 *
 * Empty when no main metric is configured OR when the active range
 * has no data for it. (`useEffectiveMainMetricId` synthesizes a
 * fallback in demo mode so the former is rare.)
 */
export interface MainMetricByDayChartProps {
  items: ReadonlyArray<KpiValueItem>;
  rows: ReadonlyArray<ScenarioRow>;
  mainMetricId: string | null;
  metricName: string | null;
  metricColor: string | null;
}

export function MainMetricByDayChart({
  items,
  rows,
  mainMetricId,
  metricName,
  metricColor,
}: MainMetricByDayChartProps): JSX.Element {
  const data = useMemo(() => buildData(items, rows, mainMetricId), [items, rows, mainMetricId]);
  const title = metricName ? `${metricName} by Day` : 'Main metric by Day';
  return (
    <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-3">
      <header className="mb-2 text-sm font-medium text-foreground/80">{title}</header>
      {data.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-foreground/40">
          {mainMetricId
            ? 'No data for this metric in the active range.'
            : 'Pick a main metric in KPI Builder to populate this chart.'}
        </div>
      ) : (
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                interval="preserveStartEnd"
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }}
                stroke="rgba(255,255,255,0.2)"
              />
              <YAxis
                tickFormatter={(v) => shortNumber(Number(v))}
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }}
                stroke="rgba(255,255,255,0.2)"
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                contentStyle={{
                  backgroundColor: 'rgba(7, 23, 48, 0.95)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  color: '#fff',
                }}
                labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                formatter={(value: number) => [value.toLocaleString(), 'Quantity']}
              />
              <Bar
                dataKey="quantity"
                fill={metricColor ?? 'hsl(220, 70%, 60%)'}
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

interface ChartRow {
  date: string;
  quantity: number;
}

function buildData(
  items: ReadonlyArray<KpiValueItem>,
  rows: ReadonlyArray<ScenarioRow>,
  mainMetricId: string | null,
): ChartRow[] {
  if (!mainMetricId || items.length === 0) return [];
  const filteredIds = new Set(rows.map((r) => r.fusionId).filter(Boolean));
  const byDay = new Map<string, number>();
  for (const it of items) {
    if (filteredIds.size > 0 && !filteredIds.has(it.fusionScenarioId)) continue;
    const counters = it.metrics[mainMetricId];
    if (!counters) continue;
    const q = counters.quantity ?? 0;
    if (q === 0) continue;
    byDay.set(it.datePart, (byDay.get(it.datePart) ?? 0) + q);
  }
  return Array.from(byDay.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, quantity]) => ({ date: shortDate(date), quantity }));
}

function shortDate(iso: string): string {
  return iso.length >= 10 ? iso.slice(5) : iso;
}

function shortNumber(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}
